import React, { useEffect, useMemo, useState } from 'react';
import type { Test } from '../../types';
import { apiService } from '../../services/apiService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { toDailyAverages } from '../../utils/dailyAverages';
import { getLastYearDateRange } from '../../utils/dateRange';

const DATE_INPUT_CLASS =
  'w-full min-w-0 [color-scheme:dark] bg-navy-900 text-white placeholder-navy-400';

interface AnalyticsResultsChartProps {
  tests: Test[];
}

const CHART_WIDTH = 680;
const CHART_HEIGHT = 280;
const PADDING = 44;

const buildPathWithGaps = (points: Array<{ x: number; y: number; valid: boolean }>) => {
  if (points.length === 0) return '';
  let path = '';
  points.forEach((point) => {
    if (!point.valid) return;
    if (!path) {
      path = `M ${point.x} ${point.y}`;
      return;
    }
    path += ` L ${point.x} ${point.y}`;
  });
  return path;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateInput = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const buildDateTicks = (start: Date, end: Date, spanDays: number): Date[] => {
  const ticks: Date[] = [];

  if (spanDays <= 90) {
    const stepDays = spanDays <= 10 ? 1 : spanDays <= 21 ? 2 : spanDays <= 45 ? 7 : 14;
    for (let cursor = new Date(start); cursor <= end; cursor = addDays(cursor, stepDays)) {
      ticks.push(new Date(cursor));
    }
  } else {
    const stepMonths = spanDays <= 366 ? 1 : spanDays <= 730 ? 2 : 3;
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor < start) cursor.setMonth(cursor.getMonth() + stepMonths, 1);
    while (cursor <= end) {
      ticks.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + stepMonths, 1);
    }
  }

  // Always keep the selected end date as the final tick.
  const endDay = startOfDay(end).getTime();
  if (!ticks.some((tick) => startOfDay(tick).getTime() === endDay)) {
    ticks.push(new Date(end));
  }

  return ticks;
};

const formatTickLabel = (date: Date, domainEnd: Date, spanDays: number) => {
  if (spanDays > 730) {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  return date.toLocaleDateString(
    'en-US',
    date.getFullYear() === domainEnd.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
};

export const AnalyticsResultsChart: React.FC<AnalyticsResultsChartProps> = ({ tests }) => {
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateRange, setDateRange] = useState(getLastYearDateRange);
  const { startDate, endDate } = dateRange;
  const [rangeTests, setRangeTests] = useState<Test[] | null>(null);

  useEffect(() => {
    if (!startDate && !endDate) {
      setRangeTests(null);
      return;
    }
    let cancelled = false;
    apiService
      .getAllTestSummaries({
        ...(startDate ? { startDate: `${startDate}T00:00:00` } : {}),
        ...(endDate ? { endDate: `${endDate}T23:59:59.999` } : {}),
      })
      .then((tests) => {
        if (!cancelled) setRangeTests(tests);
      })
      .catch(() => {
        if (!cancelled) setRangeTests(null);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const completedTests = useMemo(
    () => (rangeTests ?? tests).filter((test) => test.results),
    [tests, rangeTests]
  );

  const employeeOptions = useMemo(() => {
    const values = completedTests
      .map((test) => test.employee?.employeeId || test.employeeId)
      .filter((value): value is string => Boolean(value));
    return Array.from(new Set(values));
  }, [completedTests]);

  const shiftOptions = useMemo(() => {
    const values = completedTests
      .map((test) => test.workShift)
      .filter((value): value is NonNullable<Test['workShift']> => Boolean(value));
    return Array.from(new Set(values));
  }, [completedTests]);

  const departmentOptions = useMemo(() => {
    const values = completedTests
      .map((test) => test.department)
      .filter((value): value is string => Boolean(value));
    return Array.from(new Set(values));
  }, [completedTests]);

  const filteredTests = useMemo(() => {
    return completedTests.filter((test) => {
      const testTime = new Date(test.testDate).getTime();
      const start = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
      const end = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;
      if (start && testTime < start) return false;
      if (end && testTime > end) return false;
      const employeeId = test.employee?.employeeId || test.employeeId || '';
      const matchesEmployee = !employeeFilter || employeeId === employeeFilter;
      const matchesShift = !shiftFilter || test.workShift === shiftFilter;
      const matchesDepartment = !departmentFilter || test.department === departmentFilter;
      return matchesEmployee && matchesShift && matchesDepartment;
    });
  }, [completedTests, employeeFilter, shiftFilter, departmentFilter, startDate, endDate]);

  const chartPoints = useMemo(() => {
    const hasFilters = Boolean(employeeFilter || shiftFilter || departmentFilter || startDate || endDate);
    const sourceTests = hasFilters ? filteredTests : completedTests;

    return toDailyAverages(
      sourceTests.map((test) => ({
        date: test.testDate,
        average: test.results?.averageReactionTime ?? null,
        median: test.results?.medianReactionTime ?? null,
      }))
    ).map((point) => ({
      date: point.date,
      average: point.average,
      median: point.median,
    }));
  }, [completedTests, filteredTests, employeeFilter, shiftFilter, departmentFilter, startDate, endDate]);

  const { domainStart, domainEnd } = useMemo(() => {
    const start = parseDateInput(startDate);
    const end = parseDateInput(endDate);
    const dataTimes = chartPoints.map((point) => point.date.getTime());
    const fallbackStart = dataTimes.length ? new Date(Math.min(...dataTimes)) : addDays(new Date(), -30);
    const fallbackEnd = dataTimes.length ? new Date(Math.max(...dataTimes)) : new Date();

    const startValue = startOfDay(start ?? fallbackStart);
    let endValue = endOfDay(end ?? fallbackEnd);
    if (endValue.getTime() <= startValue.getTime()) {
      endValue = endOfDay(startValue);
    }

    return { domainStart: startValue, domainEnd: endValue };
  }, [chartPoints, startDate, endDate]);

  const spanDays = Math.max(1, (domainEnd.getTime() - domainStart.getTime()) / DAY_MS);

  const { minValue, maxValue } = useMemo(() => {
    const values = chartPoints
      .flatMap((point) => [point.average, point.median])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

    if (!values.length) {
      return { minValue: 195, maxValue: 445 };
    }

    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const padding = Math.max(20, (dataMax - dataMin) * 0.15);
    let min = Math.max(0, Math.floor((dataMin - padding) / 10) * 10);
    let max = Math.ceil((dataMax + padding) / 10) * 10;

    if (max - min < 50) {
      const mid = (max + min) / 2;
      min = Math.max(0, Math.floor((mid - 25) / 10) * 10);
      max = min + 50;
    }

    return { minValue: min, maxValue: max };
  }, [chartPoints]);
  const range = maxValue - minValue || 1;

  const mapX = (date: Date) => {
    const total = domainEnd.getTime() - domainStart.getTime();
    if (total <= 0) return CHART_WIDTH / 2;
    const ratio = Math.min(1, Math.max(0, (date.getTime() - domainStart.getTime()) / total));
    return PADDING + ratio * (CHART_WIDTH - PADDING * 2);
  };
  const mapY = (value: number) => {
    const clamped = Math.min(Math.max(value, minValue), maxValue);
    return CHART_HEIGHT - PADDING - ((clamped - minValue) / range) * (CHART_HEIGHT - PADDING * 2);
  };

  const avgPoints = chartPoints.map((point) => {
    const value = point.average;
    const valid = typeof value === 'number' && Number.isFinite(value);
    return { x: mapX(point.date), y: mapY(valid ? value! : minValue), valid };
  });
  const medPoints = chartPoints.map((point) => {
    const value = point.median ?? point.average ?? null;
    const valid = typeof value === 'number' && Number.isFinite(value);
    return { x: mapX(point.date), y: mapY(valid ? value! : minValue), valid };
  });

  const avgPath = buildPathWithGaps(avgPoints);
  const medPath = buildPathWithGaps(medPoints);

  // Build labels from the selected date range, then work backwards from the
  // final tick so the most recent date is always shown and labels never collide.
  const minLabelGap = spanDays > 120 ? 76 : 56;
  const tickDates = buildDateTicks(domainStart, domainEnd, spanDays);
  const tickLabels: Array<{ date: Date; x: number; label: string }> = [];
  let lastKeptX = Number.POSITIVE_INFINITY;
  for (let i = tickDates.length - 1; i >= 0; i -= 1) {
    const date = tickDates[i];
    const x = mapX(date);
    if (tickLabels.length === 0 || lastKeptX - x >= minLabelGap) {
      tickLabels.push({ date, x, label: formatTickLabel(date, domainEnd, spanDays) });
      lastKeptX = x;
    }
  }
  tickLabels.reverse();
  const defaultDateRange = getLastYearDateRange();
  const hasFilters = Boolean(
    employeeFilter ||
      shiftFilter ||
      departmentFilter ||
      startDate !== defaultDateRange.startDate ||
      endDate !== defaultDateRange.endDate
  );

  return (
    <div className="bg-navy-800 rounded-2xl p-4 shadow-sm border border-navy-600 sm:p-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-bold text-white">Reaction-time trends</h2>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-10 h-0.5 bg-gold-500"></div>
              <span className="text-navy-200">Average RT</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-0.5 border-t-2 border-dashed border-gold-500"></div>
              <span className="text-navy-200">Median RT</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0 sm:col-span-2">
            <Input
              aria-label="Filter by employee ID"
              value={employeeFilter}
              onChange={(event) => setEmployeeFilter(event.target.value)}
              placeholder="Search Employee ID"
              list="employee-id-options"
            />
            <datalist id="employee-id-options">
              {employeeOptions.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </div>
          <Select value={shiftFilter || 'all'} onValueChange={(value) => setShiftFilter(value === 'all' ? '' : value)}>
            <SelectTrigger aria-label="Filter by shift" className="w-full">
              <SelectValue placeholder="Shift" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shifts</SelectItem>
              {shiftOptions.map((value) => (
                <SelectItem key={value} value={value}>
                  {value.charAt(0) + value.slice(1).toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={departmentFilter || 'all'} onValueChange={(value) => setDepartmentFilter(value === 'all' ? '' : value)}>
            <SelectTrigger aria-label="Filter by department" className="w-full">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departmentOptions.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label="Filter from date"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(event) => setDateRange((range) => ({ ...range, startDate: event.target.value }))}
            className={DATE_INPUT_CLASS}
          />
          <Input
            aria-label="Filter through date"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(event) => setDateRange((range) => ({ ...range, endDate: event.target.value }))}
            className={DATE_INPUT_CLASS}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setEmployeeFilter('');
                setShiftFilter('');
                setDepartmentFilter('');
                setDateRange(getLastYearDateRange());
              }}
              className="inline-flex h-9 items-center justify-center rounded-md border border-navy-600 px-3 text-sm text-gold-400 hover:bg-navy-700 sm:justify-self-start"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {chartPoints.length === 0 ? (
        <div className="py-12 text-center text-sm text-navy-300">No test results match the selected filters.</div>
      ) : (
        <div className="mt-6 -mx-2 overflow-x-auto px-2">
          <svg
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            className="h-auto w-full min-w-[560px]"
            role="img"
            aria-label="Average and median reaction time by date"
          >
          <rect x={0} y={0} width={CHART_WIDTH} height={CHART_HEIGHT} rx={16} fill="#13214A" />

          {Array.from({ length: 5 }).map((_, idx) => {
            const y = PADDING + (idx / 4) * (CHART_HEIGHT - PADDING * 2);
            return (
              <line key={`grid-${idx}`} x1={PADDING} y1={y} x2={CHART_WIDTH - PADDING} y2={y} stroke="#28396B" strokeWidth="1" />
            );
          })}

          <line x1={PADDING} y1={PADDING} x2={PADDING} y2={CHART_HEIGHT - PADDING} stroke="#93A0C4" strokeWidth="1.5" />
          <line x1={PADDING} y1={CHART_HEIGHT - PADDING} x2={CHART_WIDTH - PADDING} y2={CHART_HEIGHT - PADDING} stroke="#93A0C4" strokeWidth="1.5" />

          {Array.from({ length: 5 }).map((_, idx) => {
            const value = maxValue - (idx / 4) * range;
            const y = PADDING + (idx / 4) * (CHART_HEIGHT - PADDING * 2);
            return (
              <text key={`y-${idx}`} x={PADDING - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#93A0C4">
                {Math.round(value)}
              </text>
            );
          })}

          {tickLabels.map((tick) => (
            <text
              key={`x-${tick.date.getTime()}`}
              x={tick.x}
              y={CHART_HEIGHT - PADDING + 18}
              textAnchor="middle"
              fontSize="11"
              fill="#93A0C4"
            >
              {tick.label}
            </text>
          ))}

          <path d={avgPath} fill="none" stroke="#D9A93B" strokeWidth="2.5" />
          <path d={medPath} fill="none" stroke="#D9A93B" strokeWidth="2.5" strokeDasharray="6 5" />

        {/* Points intentionally omitted for a cleaner line-only look */}

          <text x={PADDING - 32} y={CHART_HEIGHT / 2} fontSize="12" fill="#F4F6FB" textAnchor="middle" transform={`rotate(-90 ${PADDING - 32} ${CHART_HEIGHT / 2})`}>
            Average Reaction Time (ms)
          </text>
          <text x={(CHART_WIDTH + PADDING) / 2} y={CHART_HEIGHT - 8} fontSize="12" fill="#F4F6FB">
            Date
          </text>
        </svg>
        </div>
      )}
    </div>
  );
};
